import { useCallback, useState } from 'react';

import { AxiosError, AxiosResponse } from 'axios';
import dayjs from 'dayjs';
import { UseMutationResult } from 'react-query';
import { AthletePackage, AthletePackageChange, ErrorMessage } from 'types';
import notificationHandler from 'utils/notifications';

import { ChangeFormValues } from './useChangeForm';

export type ChangeMutation = UseMutationResult<
  AxiosResponse<AthletePackage>,
  AxiosError<ErrorMessage>,
  AthletePackageChange
>;

interface UseChangeSubmissionParams {
  values: ChangeFormValues;
  changeMutation: ChangeMutation;
  onSuccess: () => void;
}

interface ChangeSubmission {
  submit: () => void;
  errorMessage: string | null;
  resetError: () => void;
}

export function buildChangePayload(values: ChangeFormValues): AthletePackageChange | null {
  if (!values.timing || !values.captured) return null;
  const c = values.captured;
  return {
    timing: values.timing,
    newPackage: c.package.packageId,
    billingPlan: c.billingPlan.billingPlanId,
    billingStartDate: dayjs(c.billingStartDate).format('YYYY-MM-DD'),
    athleteStatusOnStart: c.athleteStatusOnStart,
    athleteStatusOnEnd: c.athleteStatusOnEnd,
    paymentMethod: c.paymentMethodId,
    nonStripePaymentMethod: c.nonStripePaymentMethod,
    autoPay: c.autoPay,
    autoRenew: c.autoRenew,
    retainReservations: values.reservationChoice !== 'DELETE',
    followUpPackage: c.followUpPackageId,
    followUpBillingPlan: c.followUpBillingPlanId,
    discount: c.discount?.discountId ?? null,
    applyToAll: c.applyDiscountToAll,
  };
}

export default function useChangeSubmission({
  values,
  changeMutation,
  onSuccess,
}: UseChangeSubmissionParams): ChangeSubmission {
  const { successNotification, errorNotification } = notificationHandler();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetError = useCallback(() => setErrorMessage(null), []);

  const submit = useCallback(() => {
    const payload = buildChangePayload(values);
    if (!payload) return;
    setErrorMessage(null);
    changeMutation.mutate(payload, {
      onError: (err: AxiosError<ErrorMessage>) => {
        const message = err.response?.data?.message ?? 'Failed to change athlete package.';
        setErrorMessage(message);
        errorNotification('Athlete package change failed', message);
      },
      onSuccess: () => {
        successNotification('Athlete package change success', 'Athlete package successfully changed.');
        onSuccess();
      },
    });
  }, [values, changeMutation, successNotification, errorNotification, onSuccess]);

  return { submit, errorMessage, resetError };
}
